import { BaseInput } from './BaseInput';
import { IconInfoCircle } from '@tabler/icons-react';
import React, { forwardRef } from 'react';
import { tv, VariantProps } from 'tailwind-variants';
import { FieldWrapper } from './FieldWrapper';

const style = tv({
  variants: {
    rounded: {
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
      '3xl': 'rounded-3xl',
      full: 'rounded-full',
    },
    error: {
      true: 'border-red-600',
    },
    disabled: {
      true: 'bg-rw-neutral-off-white',
    },
    appearance: {
      default: '',
      'design-system':
        'h-11 rounded-xl border-[1.5px] border-rw-neutral-off-white bg-white p-2.5 text-base leading-6 text-rw-neutral-darker-gray !shadow-none placeholder:text-rw-neutral-light-gray autofill:bg-white hover:border-rw-sub focus:border-primary-teal focus:ring-0',
    },
  },
  compoundVariants: [
    {
      appearance: 'design-system',
      error: true,
      className: 'border-rw-red hover:border-rw-red focus:border-rw-red',
    },
    {
      appearance: 'design-system',
      disabled: true,
      className:
        'border-rw-neutral-lighter-gray bg-rw-neutral-off-white opacity-60',
    },
  ],
  defaultVariants: {
    rounded: 'md',
    appearance: 'default',
  },
});

type InputFieldProps = Omit<React.ComponentProps<typeof BaseInput>, 'label'> &
  VariantProps<typeof style> & {
    label?: React.ReactNode;
    errors?: string[];
    appearance?: 'default' | 'design-system';
  };

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      type = 'text',
      label,
      rounded,
      className,
      icon,
      error,
      errors,
      appearance = 'default',
      ...props
    },
    ref
  ) => {
    return (
      <FieldWrapper label={label} appearance={appearance}>
        <BaseInput
          ref={ref}
          type={type}
          data-field-appearance={appearance}
          className={style({
            error,
            rounded,
            className,
            disabled: props.disabled,
            appearance,
          })}
          icon={icon}
          {...props}
        />

        {error && errors && (
          <div className="mt-1 flex-col items-center text-sm font-normal text-rw-red">
            {errors.map((message: string) => (
              <div key={message} className="flex">
                <IconInfoCircle className="mr-2 mt-0.5 size-4 shrink-0" />
                {message}
              </div>
            ))}
          </div>
        )}
      </FieldWrapper>
    );
  }
);

InputField.displayName = 'InputField';
